import { useEffect, useState } from "react";
import api from "@/api/client";
import { ShieldCheck, Building2, Globe } from "lucide-react";

/**
 * Inward category radio: Regulator | Within Organization | External.
 * When Regulator is picked, auto-toggles priority=true and shows a Regulator dropdown.
 * Priority stays user-controllable after auto-toggle.
 */
export default function InwardCategoryRadio({ value, onChange, regulatorId, onRegulatorChange, onPriorityAutoToggle }) {
  const [regulators, setRegulators] = useState([]);
  useEffect(() => {
    api.get("/regulators").then(r => setRegulators(r.data || [])).catch(() => setRegulators([]));
  }, []);

  const options = [
    { v: "Regulator", label: "Regulator", desc: "Govt / Bank / Court / Police — auto-priority", Icon: ShieldCheck },
    { v: "Within Organization", label: "Within organization", desc: "Same tenant, inter-branch or interdept", Icon: Building2 },
    { v: "External", label: "External", desc: "Any other external sender", Icon: Globe },
  ];

  const pick = (v) => {
    onChange(v);
    if (v === "Regulator" && onPriorityAutoToggle) onPriorityAutoToggle(true);
  };

  return (
    <div className="space-y-2" data-testid="inward-category">
      <div className="overline">Inward category</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {options.map(({ v, label, desc, Icon }) => {
          const active = value === v;
          return (
            <label key={v} data-testid={`inward-cat-${v.replace(/\s+/g, "-").toLowerCase()}`}
              className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition-colors ${
                active ? "border-primary bg-red-50 shadow-brut-orange" : "border-border hover:border-primary/40"
              }`}>
              <input type="radio" checked={active} onChange={() => pick(v)} className="mt-1 accent-[#BE1E1E]" />
              <div className="flex-1">
                <div className="font-semibold text-sm flex items-center gap-1.5"><Icon size={12}/> {label}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{desc}</div>
              </div>
            </label>
          );
        })}
      </div>
      {value === "Regulator" && (
        <label className="block mt-1" data-testid="inward-regulator-select-wrap">
          <span className="overline text-primary">Regulator *</span>
          <select data-testid="inward-regulator-select"
            value={regulatorId || ""}
            onChange={(e) => onRegulatorChange(e.target.value)}
            className="mt-1 w-full h-10 px-3 rounded-md border border-primary bg-red-50/40 text-sm">
            <option value="">— pick regulator —</option>
            {regulators.map(r => (
              <option key={r.id} value={r.id}>
                {r.name}{r.full_name ? ` — ${r.full_name}` : ""}{r.type ? ` · ${r.type}` : ""}
              </option>
            ))}
          </select>
          {regulators.length === 0 && (
            <div className="text-[10px] text-muted-foreground mt-1">
              No regulators configured yet. Add them under Settings → Regulators.
            </div>
          )}
        </label>
      )}
    </div>
  );
}
